import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useOutletContext, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  DndContext, useDroppable, useDraggable, PointerSensor, useSensor, useSensors, DragOverlay,
} from '@dnd-kit/core';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Diamond, Flag, Plus } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import TaskSlideOver from './TaskSlideOver';
import {
  dateKey, buildMonthGrid, stylesForTarea,
  DOW_LABELS, MONTH_LABELS, refTareaUrl, tareaPorRefUrl,
} from './utils/helpers';
import { AsignadosStack, HITO_COLOR_DEFAULT, hitoEstado } from './utils/displays';

// ----- Pill de tarea (draggable) -----

const TareaPill = ({ tarea, onClick, color }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `tarea-${tarea.id}`,
    data: { tarea },
  });
  const { bg, txt, dot } = stylesForTarea(tarea);
  const asignados = tarea.asignados || [];
  const asignadosLabel = asignados.length ? ` · ${asignados.map((a) => a.nombre).join(', ')}` : '';

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        if (e.detail === 0) return;
        onClick?.(tarea);
      }}
      title={`${tarea.codigo || `${tarea.proyecto_slug}-${tarea.numero}`} · ${tarea.titulo}${asignadosLabel}`}
      className={`group relative flex items-center gap-1 px-1.5 py-1 rounded text-[11px] cursor-pointer active:cursor-grabbing hover:ring-1 hover:ring-slate-900/10 transition-all ${bg} ${isDragging ? 'opacity-0' : ''}`}
    >
      <span className={`w-1 h-1 rounded-full flex-shrink-0 ${dot}`} />
      {tarea.prioridad === 'urgente' && (
        <Flag size={9} strokeWidth={2.5} className="text-rose-500 flex-shrink-0" style={{ fill: 'currentColor' }} />
      )}
      {tarea.prioridad === 'alta' && (
        <Flag size={9} strokeWidth={2.5} className="text-orange-500 flex-shrink-0" style={{ fill: 'currentColor' }} />
      )}
      <span className={`flex-1 truncate ${txt}`}>{tarea.titulo}</span>
      {asignados.length > 0 && (
        <AsignadosStack asignados={asignados} color={color} size={14} max={2} />
      )}
    </div>
  );
};

// ----- Marca de hito (milestone) -----
// Distinta de las pills de tarea: rombo (Diamond) + nombre, color propio del hito,
// sin avatar ni prioridad. Borde izquierdo solido para leerse como una marca de
// fecha. Rose si vencido y pendiente; relleno/solido si completado.

const HitoPill = ({ hito }) => {
  const { vencido } = hitoEstado(hito);
  const c = vencido ? '#e11d48' : (hito.color || HITO_COLOR_DEFAULT);
  return (
    <div
      title={`Hito · ${hito.nombre}${hito.completado ? ' · completado' : vencido ? ' · vencido' : ''}`}
      className="flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded text-[10px] font-semibold leading-none border-l-2"
      style={{
        backgroundColor: hito.completado ? `${c}29` : `${c}14`,
        color: c,
        borderColor: c,
      }}
    >
      <Diamond
        size={9}
        strokeWidth={2.5}
        className="flex-shrink-0"
        style={{ fill: hito.completado ? 'currentColor' : 'none' }}
      />
      <span className={`flex-1 truncate ${hito.completado ? 'line-through opacity-80' : ''}`}>{hito.nombre}</span>
    </div>
  );
};

// ----- Celda de día -----

const VISIBLE_LIMIT = 3;

const DayCell = ({ cell, cellIndex, tareas, hitos, onCardClick, onCreate, color }) => {
  const key = dateKey(cell.date);
  // Sin onCreate (vista general) la celda no ofrece crear: una tarea siempre
  // nace dentro de un proyecto.
  const puedeCrear = !!onCreate;
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${key}`,
    data: { fecha: key },
  });
  const [creating, setCreating] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef(null);
  // Filas 5 y 6 (índices 28-41): abrir popover de overflow hacia arriba para no pisar
  // la siguiente fila (o salirse del viewport).
  const openUpward = cellIndex >= 28;

  useEffect(() => {
    if (!overflowOpen) return;
    const onClick = (e) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target)) {
        setOverflowOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [overflowOpen]);

  const submit = async () => {
    const t = titulo.trim();
    if (!t) { setCreating(false); return; }
    await onCreate(key, t);
    setTitulo('');
    setCreating(false);
  };

  const visibles = tareas.slice(0, VISIBLE_LIMIT);
  const ocultas = tareas.slice(VISIBLE_LIMIT);
  const showCreateHint = puedeCrear && cell.inMonth && tareas.length === 0 && hitos.length === 0 && !creating;

  return (
    <div
      ref={setNodeRef}
      onClick={(e) => {
        if (e.target === e.currentTarget && cell.inMonth && puedeCrear) setCreating(true);
      }}
      className={`group/cell relative border-r border-b border-slate-200 p-2 flex flex-col transition-colors ${
        !cell.inMonth ? 'bg-slate-100/70' : cell.isToday ? 'bg-cyan-50/60' : ''
      } ${cell.inMonth && puedeCrear ? 'cursor-pointer' : ''} ${isOver ? '!bg-cyan-50 ring-2 ring-cyan-400/50 ring-inset' : ''}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span
          className={`text-[11px] tabular-nums ${
            cell.isToday
              ? 'inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-white font-semibold'
              : cell.inMonth
              ? 'text-slate-500 font-medium'
              : 'text-slate-300'
          }`}
        >
          {cell.date.getDate()}
        </span>
        {cell.inMonth && puedeCrear && !creating && (
          <button
            onClick={(e) => { e.stopPropagation(); setCreating(true); }}
            className="opacity-0 group-hover/cell:opacity-100 text-slate-300 hover:text-slate-700 transition-all"
            title="Crear tarea con esta fecha"
          >
            <Plus size={11} strokeWidth={2.5} />
          </button>
        )}
      </div>

      <div className="space-y-1">
        {hitos.map((h) => (
          <HitoPill key={`hito-${h.id}`} hito={h} />
        ))}

        {visibles.map((t) => (
          <TareaPill key={t.id} tarea={t} onClick={onCardClick} color={color} />
        ))}

        {/* Overflow */}
        {ocultas.length > 0 && (
          <div className="relative" ref={overflowRef}>
            <button
              onClick={(e) => { e.stopPropagation(); setOverflowOpen((v) => !v); }}
              className="w-full text-left px-1.5 py-0.5 text-[10px] font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded transition-colors"
            >
              + {ocultas.length} más
            </button>
            <AnimatePresence>
              {overflowOpen && (
                <motion.div
                  initial={{ opacity: 0, y: openUpward ? 4 : -4, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: openUpward ? 4 : -4, scale: 0.97 }}
                  transition={{ duration: 0.12 }}
                  className={`absolute left-0 w-64 bg-white rounded-lg shadow-2xl ring-1 ring-slate-200 overflow-hidden z-20 ${
                    openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
                  }`}
                >
                  <div className="px-3 py-2 border-b border-slate-200/70 flex items-center justify-between">
                    <span className="text-[10px] font-semibold tracking-widest text-slate-500 uppercase">
                      {cell.date.toLocaleDateString('es-VE', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </span>
                    <span className="text-[10px] text-slate-400 tabular-nums">{tareas.length}</span>
                  </div>
                  <div className="p-1.5 space-y-1 max-h-72 overflow-y-auto">
                    {tareas.map((t) => (
                      <TareaPill key={t.id} tarea={t} onClick={(tarea) => { setOverflowOpen(false); onCardClick(tarea); }} color={color} />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {creating && (
          <input
            autoFocus
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            onBlur={submit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); submit(); }
              if (e.key === 'Escape') { setCreating(false); setTitulo(''); }
            }}
            placeholder="Título..."
            className="w-full px-1.5 py-1 text-[11px] bg-white border border-cyan-400 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500/30"
          />
        )}

        {showCreateHint && (
          <span className="block text-[10px] text-slate-300 px-1 opacity-0 group-hover/cell:opacity-100 transition-opacity">
            Click para crear
          </span>
        )}
      </div>
    </div>
  );
};

// ----- Vista calendario -----

const ProyectoCalendarioView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    proyecto, tareas, refetchTareas, updateTareaLocal, addTareaLocal, color,
    // Vista general: tareas de todos los proyectos, sin creacion inline.
    esVistaGeneral = false, proyectosMap,
  } = useOutletContext();

  const [current, setCurrent] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [activeDrag, setActiveDrag] = useState(null);
  const [slideOverTareaId, setSlideOverTareaId] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const { cells } = useMemo(() => buildMonthGrid(current.year, current.month), [current]);

  // Tareas agrupadas por fecha key
  const tareasPorDia = useMemo(() => {
    const m = {};
    (tareas || []).forEach((t) => {
      if (!t.fecha_vencimiento) return;
      if (!m[t.fecha_vencimiento]) m[t.fecha_vencimiento] = [];
      m[t.fecha_vencimiento].push(t);
    });
    return m;
  }, [tareas]);

  // Hitos del proyecto agrupados por fecha key. Se pintan como marca especial en
  // la celda de su 'fecha', por encima de las tareas.
  const hitosPorDia = useMemo(() => {
    const m = {};
    (proyecto?.hitos || []).forEach((h) => {
      if (!h.fecha) return;
      if (!m[h.fecha]) m[h.fecha] = [];
      m[h.fecha].push(h);
    });
    return m;
  }, [proyecto]);

  // ----- Drag handlers -----

  const handleDragStart = (event) => {
    setActiveDrag(event.active.data?.current?.tarea || null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDrag(null);
    if (!over) return;

    const tarea = active.data?.current?.tarea;
    const nuevaFecha = over.data?.current?.fecha;
    if (!tarea || !nuevaFecha) return;
    if (tarea.fecha_vencimiento === nuevaFecha) return;

    updateTareaLocal(tarea.id, { fecha_vencimiento: nuevaFecha });
    try {
      await axiosInstance.put(`/api/tareas/${tarea.id}`, { fecha_vencimiento: nuevaFecha });
      // Si el drop fue a un dia fuera del mes actual, navegar al mes destino.
      // Asi la tarea queda visible y el usuario no piensa que "desaparecio".
      const d = new Date(nuevaFecha + 'T00:00:00');
      if (d.getFullYear() !== current.year || d.getMonth() !== current.month) {
        setCurrent({ year: d.getFullYear(), month: d.getMonth() });
      }
    } catch (err) {
      console.error('Error moviendo:', err);
      updateTareaLocal(tarea.id, { fecha_vencimiento: tarea.fecha_vencimiento });
    }
  };

  // ----- Crear tarea con fecha -----

  const handleCreate = async (fecha, titulo) => {
    if (!proyecto) return;
    const porHacer = proyecto.estados.find((e) => e.categoria === 'por_hacer');
    try {
      const { data } = await axiosInstance.post(`/api/proyectos/${proyecto.id}/tareas`, {
        titulo,
        fecha_vencimiento: fecha,
        estado_id: porHacer?.id,
      });
      addTareaLocal(data);
    } catch (err) {
      console.error('Error creando:', err);
    }
  };

  // ----- Navegacion -----

  const prevMonth = useCallback(() => {
    setCurrent((c) => ({
      year: c.month === 0 ? c.year - 1 : c.year,
      month: c.month === 0 ? 11 : c.month - 1,
    }));
  }, []);
  const nextMonth = useCallback(() => {
    setCurrent((c) => ({
      year: c.month === 11 ? c.year + 1 : c.year,
      month: c.month === 11 ? 0 : c.month + 1,
    }));
  }, []);
  const goToday = useCallback(() => {
    const d = new Date();
    setCurrent({ year: d.getFullYear(), month: d.getMonth() });
  }, []);

  // Atajos: ←/→ navega meses, T va a hoy. Solo cuando no hay slide-over abierto y
  // el foco no está en un input/textarea.
  useEffect(() => {
    if (slideOverTareaId) return;
    const onKey = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); prevMonth(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); nextMonth(); }
      else if (e.key.toLowerCase() === 't') { e.preventDefault(); goToday(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [slideOverTareaId, prevMonth, nextMonth, goToday]);

  // ----- Slide over -----

  useEffect(() => {
    const match = location.pathname.match(/\/proyectos\/[^/]+\/calendario\/tareas\/(\d+)/);
    if (match) {
      const ref = parseInt(match[1], 10);
      const t = tareaPorRefUrl(tareas, ref, esVistaGeneral);
      if (t) setSlideOverTareaId(t.id);
    } else {
      setSlideOverTareaId(null);
    }
  }, [tareas, location.pathname, esVistaGeneral]);

  const handleCardClick = useCallback((tarea) => {
    setSlideOverTareaId(tarea.id);
    navigate(`/proyectos/${slug}/calendario/tareas/${refTareaUrl(tarea, esVistaGeneral)}`, { replace: false });
  }, [navigate, slug, esVistaGeneral]);

  const handleCloseSlideOver = useCallback(() => {
    setSlideOverTareaId(null);
    navigate(`/proyectos/${slug}/calendario`, { replace: false });
  }, [navigate, slug]);

  // Orden visual: por fecha de vencimiento, las sin fecha al final
  const tareasOrdenadas = useMemo(() => {
    return [...(tareas || [])].sort((a, b) => {
      if (!a.fecha_vencimiento && !b.fecha_vencimiento) return 0;
      if (!a.fecha_vencimiento) return 1;
      if (!b.fecha_vencimiento) return -1;
      return a.fecha_vencimiento.localeCompare(b.fecha_vencimiento);
    });
  }, [tareas]);

  const handleNavigateSlideOver = useCallback((dir) => {
    if (!slideOverTareaId) return;
    const idx = tareasOrdenadas.findIndex((t) => t.id === slideOverTareaId);
    if (idx === -1) return;
    const nextIdx = dir === 'next' ? idx + 1 : idx - 1;
    if (nextIdx < 0 || nextIdx >= tareasOrdenadas.length) return;
    const next = tareasOrdenadas[nextIdx];
    setSlideOverTareaId(next.id);
    navigate(`/proyectos/${slug}/calendario/tareas/${refTareaUrl(next, esVistaGeneral)}`, { replace: false });
  }, [slideOverTareaId, tareasOrdenadas, navigate, slug, esVistaGeneral]);

  // El slide-over necesita la config del proyecto REAL de la tarea; en la vista
  // general el del contexto es sintetico (union de todos).
  const proyectoDelSlideOver = useMemo(() => {
    if (!esVistaGeneral || !slideOverTareaId) return proyecto;
    const t = (tareas || []).find((x) => x.id === slideOverTareaId);
    return (t && proyectosMap?.[t.proyecto_id]) || proyecto;
  }, [esVistaGeneral, slideOverTareaId, tareas, proyectosMap, proyecto]);

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 pt-4 pb-3 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <button
            onClick={prevMonth}
            title="Mes anterior (←)"
            className="p-1 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded transition-colors"
          >
            <ChevronLeft size={14} />
          </button>
          <h3 className="text-sm font-semibold tracking-tight text-slate-900 tabular-nums min-w-[120px] text-center">
            {MONTH_LABELS[current.month]} {current.year}
          </h3>
          <button
            onClick={nextMonth}
            title="Mes siguiente (→)"
            className="p-1 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded transition-colors"
          >
            <ChevronRight size={14} />
          </button>
          <button
            onClick={goToday}
            title="Ir a hoy (T)"
            className="ml-2 px-2 py-1 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
          >
            Hoy
          </button>
        </div>
        <span className="text-xs text-slate-400 tabular-nums">
          {Object.values(tareasPorDia).flat().length} con fecha
        </span>
      </div>

      {/* Calendario */}
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex-1 overflow-hidden px-6 py-4 min-h-0">
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white h-full flex flex-col">
            {/* Header dias de semana */}
            <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-100/60 flex-shrink-0">
              {DOW_LABELS.map((d) => (
                <div
                  key={d}
                  className="px-2 py-1.5 text-[10px] font-semibold tracking-wider text-slate-500 uppercase text-center"
                >
                  {d}
                </div>
              ))}
            </div>

            {/* Celdas: grid-rows-6 con 1fr → cada fila toma alto proporcional del viewport */}
            <div className="grid grid-cols-7 grid-rows-6 flex-1 min-h-0">
              {cells.map((cell, i) => {
                const key = dateKey(cell.date);
                const tareasCelda = tareasPorDia[key] || [];
                const hitosCelda = hitosPorDia[key] || [];
                return (
                  <DayCell
                    key={i}
                    cell={cell}
                    cellIndex={i}
                    tareas={tareasCelda}
                    hitos={hitosCelda}
                    onCardClick={handleCardClick}
                    onCreate={esVistaGeneral ? null : handleCreate}
                    color={color}
                  />
                );
              })}
            </div>
          </div>
        </div>

        <DragOverlay dropAnimation={null}>
          {activeDrag ? (
            <div className="rotate-3 cursor-grabbing shadow-xl rounded ring-1 ring-slate-900/10">
              <TareaPill tarea={activeDrag} color={color} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <TaskSlideOver
        tareaId={slideOverTareaId}
        proyecto={proyectoDelSlideOver}
        onClose={handleCloseSlideOver}
        onUpdate={(tareaCompleta) => updateTareaLocal?.(tareaCompleta.id, tareaCompleta)}
        onArchive={() => { refetchTareas(); handleCloseSlideOver(); }}
        onDuplicate={(nueva) => { refetchTareas(); setSlideOverTareaId(nueva.id); }}
        onNavigate={handleNavigateSlideOver}
      />
    </div>
  );
};

export default ProyectoCalendarioView;
